/**
 * AuthContext.js
 * ─────────────────────────────────────────────────────────────────────────────
 * AUTENTICACIÓN — Fuente de verdad única para el usuario en sesión.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │  INICIALIZACIÓN DE SESIÓN (/auth/me)                                     │
 * │  • Se llama una vez al montar AuthProvider (useEffect → checkAuth).      │
 * │  • Usa withCredentials:true → la cookie httpOnly de sesión se envía      │
 * │    automáticamente. No hay token en localStorage; todo es cookie.        │
 * │  • Si /auth/me devuelve 200 → user queda en estado.                     │
 * │  • Si /auth/me devuelve 401 → user = null. La app NO se rompe:          │
 * │      - Rutas públicas renderizan normalmente.                            │
 * │      - ProtectedRoute redirige a /login cuando user===null.             │
 * │                                                                          │
 * │  RUTAS QUE SALTAN EL CHECK (sin llamar a /auth/me):                     │
 * │  • #session_id=  → callback del Google Auth de Emergent.                │
 * │  • /google-drive/callback → callback de OAuth de Google Drive.          │
 * │    En ambos casos la sesión aún no está establecida; la página de       │
 * │    callback la gestiona ella misma. Sin esta guardia Chrome mostraría   │
 * │    un 401 en consola y la app podría quedar en blanco.                  │
 * │                                                                          │
 * │  MANEJO DE 401 EN /google-drive/callback:                               │
 * │  • GoogleDriveCallbackPage procesa el código OAuth sin cookie.          │
 * │  • Si la sesión expiró → la callback muestra mensaje claro + botón     │
 * │    "Iniciar sesión". No se produce pantalla negra.                      │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * `API` viene de `/lib/api.js` (única fuente de verdad). Construye la base
 * a partir de `window.location.origin`, así que las cookies httpOnly
 * funcionan igual en preview y en producción sin configuración adicional.
 */
import {
  createContext, useContext, useState, useEffect, useCallback,
} from "react";
import axios from "axios";
import { API } from "../lib/api";

// ── Rutas donde NO se debe intentar /auth/me ──────────────────────────────────
// Añade aquí cualquier ruta pública donde la sesión no esté garantizada.
const SKIP_AUTH_CHECK_PATHS = new Set(["/google-drive/callback"]);

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  // loading=true mientras se resuelve el estado de sesión inicial.
  // ProtectedRoute muestra un spinner hasta que loading sea false.
  const [loading, setLoading] = useState(true);

  // ── checkAuth ─────────────────────────────────────────────────────────────
  // Comprueba si hay sesión activa llamando a GET /api/auth/me.
  // Se llama al montar y se expone para que otros componentes puedan
  // refrescar manualmente (e.g., después de un login manual).
  const checkAuth = useCallback(async () => {
    // ── Guardia 1: callback de Google Auth (Emergent) ─────────────────────
    // La URL contiene #session_id= justo al volver de Google OAuth.
    // AuthCallback.jsx intercambia el session_id y establece la cookie;
    // llamar /auth/me aquí daría 401 porque la cookie aún no existe.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }

    // ── Guardia 2: callback de Google Drive OAuth ─────────────────────────
    // /google-drive/callback recibe el `code` de Google y lo procesa
    // mediante un token de estado almacenado en MongoDB (sin cookie).
    // Si la sesión expiró durante el flujo OAuth, GoogleDriveCallbackPage
    // gestiona ese caso mostrando un mensaje claro en vez de pantalla en blanco.
    // Llamar /auth/me aquí generaría un 401 innecesario en consola.
    if (SKIP_AUTH_CHECK_PATHS.has(window.location.pathname)) {
      setLoading(false);
      return;
    }

    // ── Llamada principal a /api/auth/me ───────────────────────────────────
    // withCredentials:true → la cookie httpOnly de sesión se adjunta
    // automáticamente por el navegador.
    // Si el backend devuelve 401/403 (sesión inexistente o expirada):
    //   • user queda a null → las rutas públicas renderizan sin problema.
    //   • ProtectedRoute detecta user===null y redirige a /login.
    //   • La app NUNCA se rompe ni se queda en blanco por un 401.
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch (err) {
      // 401 Unauthorized o 403 Forbidden → sesión inexistente o expirada.
      // También ocurre en modo Testing de Google Drive si el usuario no está
      // en la whitelist; en ese caso el backend podría devolver 401/403.
      // En todos los casos: dejar user=null y continuar sin romper la app.
      setUser(null);
      // Solo logear en desarrollo para no saturar producción
      if (process.env.NODE_ENV === "development") {
        const status = err?.response?.status;
        if (status && status !== 401) {
          // eslint-disable-next-line no-console
          console.warn(`[AuthContext] /auth/me devolvió ${status}`);
        }
      }
    } finally {
      // Independientemente del resultado, dejar de mostrar el spinner.
      setLoading(false);
    }
  }, []);

  // Ejecutar checkAuth una sola vez al montar la app
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // ── logout ─────────────────────────────────────────────────────────────────
  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Si el logout falla (sesión ya expirada), limpiar estado igualmente
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
